import React, { useEffect, useState } from 'react';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import SvgTagEdit from '@components/ringil3/icons/TagEdit.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { InputText } from 'primereact/inputtext';
import { Field } from '@components/framework/formfield/Field.tsx';
import InputCountry from '@components/framework/input/InputCountry/InputCountry.tsx';
import InputTimezone from '@components/ringil3/Input/InputTimezone/InputTimezone.tsx';
import { useApplyResolvedTimezone } from '@hooks/useResolveTimezone/useResolveTimezone';

export interface LocationAddressValue {
  streetNr: string;
  city: string;
  postalCode: string;
  country: string;
  timezoneId?: string;
}

interface ComponentProps {
  secured?: SecuredConfig;
  value: LocationAddressValue;
  onChange: (value: LocationAddressValue) => void;
  icon?: React.ReactElement;
  readOnly?: boolean;
  dataTestId?: string;
}

const LocationAddressTag: React.FC<ComponentProps> = ({ secured, value, onChange, icon, readOnly, dataTestId }) => {
  const { tr } = useTranslationLgs();
  const [internalValue, setInternalValue] = useState<LocationAddressValue>(value);

  useEffect(() => setInternalValue(value), [value.streetNr, value.city, value.postalCode, value.country, value.timezoneId]);

  useApplyResolvedTimezone({ countryCode3: internalValue.country, city: internalValue.city, postalCode: internalValue.postalCode }, tz =>
    setInternalValue(v => ({ ...v, timezoneId: tz })),
  );

  const displayValue = [value.country, value.postalCode, value.city, value.streetNr].filter(v => !!v).join(' | ');

  const pane = (
    <div className="flex flex-col gap-2">
      <Field label={tr('generic.streetNr', 'Street and house number')}>
        <InputText value={internalValue.streetNr} onChange={e => setInternalValue(v => ({ ...v, streetNr: e.target.value }))} />
      </Field>
      <div className="flex flex-row gap-2">
        <Field label={tr('generic.city', 'City')}>
          <InputText value={internalValue.city} onChange={e => setInternalValue(v => ({ ...v, city: e.target.value }))} />
        </Field>
        <Field label={tr('generic.postalCode', 'Postal code')}>
          <InputText value={internalValue.postalCode} onChange={e => setInternalValue(v => ({ ...v, postalCode: e.target.value }))} />
        </Field>
      </div>
      <div className="flex flex-row gap-2">
        <Field label={tr('generic.country', 'Country')}>
          <InputCountry value={internalValue.country} onChange={v => setInternalValue(prev => ({ ...prev, country: v }))} />
        </Field>
        <Field label={tr('generic.timezone', 'Timezone')}>
          <InputTimezone value={internalValue.timezoneId} onChange={v => setInternalValue(prev => ({ ...prev, timezoneId: v }))} />
        </Field>
      </div>
    </div>
  );

  return (
    <ButtonTag
      secured={secured}
      label={tr('generic.address', 'Address')}
      icon={icon}
      value={displayValue}
      actionIcon={<SvgTagEdit />}
      actionPane={pane}
      popupWidth={'420px'}
      onSave={onSuccess => {
        onChange(internalValue);
        onSuccess();
      }}
      readonly={readOnly}
      autoClose
      dataTestId={dataTestId}
    />
  );
};

export default LocationAddressTag;
